import { Helmet } from "react-helmet-async";
import { useState, useMemo, useCallback } from "react";
import {
  Card,
  Container,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Drawer,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Business,
  AdminPanelSettings,
  Close,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import CustomTable from "../components/data-table/CustomDataTable";
import Protected from "../protected/Protected";
import Scrollbar from "../components/scrollbar";
import Label from "../components/label/Label";
import {
  useGetActiveSponsoredUsersQuery,
  useGetNonExistentSponsoredUsersQuery,
  useGetConsolidatedSponsoredUsersQuery,
  useUpdateSponsoredUserMutation,
  useDeleteSponsoredUserMutation,
  useDeleteSponsoredUsersBulkMutation,
  useApproveSponsoredUserMutation,
  useRejectSponsoredUserMutation,
} from "../redux/services/sponsorship.service";
import { useGetOrganizationsQuery } from "../redux/services/organization.service";
import AddBeneficiaryForm from "../sections/@dashboard/organization-sponsorship/AddBeneficiaryForm";
import StatusSummaryCards from "../sections/@dashboard/organization-sponsorship/StatusSummaryCards";
import { selectUser } from "../redux/slices/auth.slice";

export default function BeneficiariesPage() {
  const [openForm, setOpenForm] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Status Tab for Org Admin (0: All, 1: Pending, 2: In Process, 3: Approved, 4: Rejected)
  const [statusTabValue, setStatusTabValue] = useState(0);

  // Added By Filter state ("all", "admin", "organization", "user")
  const [addedByFilter, setAddedByFilter] = useState("all");

  // Filters for Existent tab (Admin/Support view)
  const [existentOrgId, setExistentOrgId] = useState("");
  const [existentStatus, setExistentStatus] = useState("");

  // Filters for Non-Existent tab (Tab 1)
  const [nonExistentOrgId, setNonExistentOrgId] = useState("");
  const [nonExistentStatus, setNonExistentStatus] = useState("");

  // Detail Drawer state
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const user = useSelector(selectUser);
  const roles = user?.roles?.map((r) => r.name) || [];
  const isAdminOrSupport = roles.some((r) => ["admin", "superadmin", "support"].includes(r));
  const isSupport = roles.includes("support");
  const isOrgAdmin = roles.includes("organization");
  const isOrgOrSupport = isOrgAdmin || isSupport;
  const userOrgId = user?.organizationId || localStorage.getItem("organizationId") || "";

  const { enqueueSnackbar } = useSnackbar();

  // Mutations
  const [updateSponsoredUser, { isLoading: isUpdating }] = useUpdateSponsoredUserMutation();
  const [deleteSponsoredUser, { isLoading: isDeleting }] = useDeleteSponsoredUserMutation();
  const [deleteSponsoredUsersBulk, { isLoading: isBulkDeleting }] = useDeleteSponsoredUsersBulkMutation();
  const [approveSponsoredUser, { isLoading: isApproving }] = useApproveSponsoredUserMutation();
  const [rejectSponsoredUser, { isLoading: isRejecting }] = useRejectSponsoredUserMutation();

  // States for delete action
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // States for bulk delete
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedIdsForDelete, setSelectedIdsForDelete] = useState([]);

  // States for edit action
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editErrors, setEditErrors] = useState({});

  // Validation regexes (same rules as Add form)
  const EDIT_NAME_REGEX = /^[a-zA-Z\u00C0-\u024F][a-zA-Z\u00C0-\u024F\s]{1,49}$/;
  const EDIT_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateEdit = () => {
    const errs = {};
    const name = editName.trim();
    const phone = editContactNumber.trim();
    const email = editEmail.trim();

    if (name) {
      if (name.length < 2) errs.editName = "Name must be at least 2 characters";
      else if (name.length > 50) errs.editName = "Name must not exceed 50 characters";
      else if (!EDIT_NAME_REGEX.test(name)) errs.editName = "Name may only contain letters and spaces";
    }
    if (phone) {
      if (!/^\d{10}$/.test(phone)) errs.editContactNumber = "Contact number must be exactly 10 digits";
      else if (/^(\d)\1{9}$/.test(phone)) errs.editContactNumber = "Enter a valid contact number";
    }
    if (email) {
      if (email.length > 254) errs.editEmail = "Email must not exceed 254 characters";
      else if (!EDIT_EMAIL_REGEX.test(email)) errs.editEmail = "Enter a valid email address";
    }

    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenEdit = useCallback((row) => {
    setEditTarget(row);
    setEditName(row.name || "");
    setEditContactNumber(row.contactNumber || "");
    setEditEmail(row.email || "");
    setEditErrors({});
    setEditDialogOpen(true);
  }, []);

  const handleOpenDelete = useCallback((row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleOpenBulkDelete = (selectedIds) => {
    setSelectedIdsForDelete(selectedIds);
    setBulkDeleteDialogOpen(true);
  };

  const handleApproveDirectly = useCallback(async (row) => {
    try {
      const response = await approveSponsoredUser(row.id).unwrap();
      if (response?.success) {
        const isNonExistent = !row.userId;
        const baseMessage = response.message || "Sponsored user approved successfully";
        const message = isNonExistent
          ? `${baseMessage}. The user is redirected for registration.`
          : baseMessage;
        enqueueSnackbar(message, { variant: "success" });
        if (openDrawer && selectedRow?.id === row.id) {
          setOpenDrawer(false);
          setSelectedRow(null);
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "Failed to approve sponsored user", {
        variant: "error",
      });
    }
  }, [approveSponsoredUser, enqueueSnackbar, openDrawer, selectedRow]);

  const handleRejectDirectly = useCallback((row) => {
    setRejectRow(row);
    setRejectReason("");
    setRejectDialogOpen(true);
  }, []);

  const confirmRejectDirectly = async () => {
    if (!rejectRow) return;
    try {
      const response = await rejectSponsoredUser({ id: rejectRow.id, reason: rejectReason }).unwrap();
      if (response?.success) {
        enqueueSnackbar(response.message || "Sponsored user rejected successfully", {
          variant: "success",
        });
        setRejectDialogOpen(false);
        setRejectRow(null);
        setRejectReason("");
        if (openDrawer && selectedRow?.id === rejectRow.id) {
          setOpenDrawer(false);
          setSelectedRow(null);
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "Failed to reject sponsored user", {
        variant: "error",
      });
    }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    if (!validateEdit()) return;
    try {
      // Only send fields that have a non-empty value; sending an empty string
      // would overwrite existing DB data with a blank.
      const updateBody = {};
      if (editName.trim()) updateBody.name = editName.trim();
      if (editContactNumber.trim()) updateBody.contactNumber = editContactNumber.trim();
      if (editEmail.trim()) updateBody.email = editEmail.trim();

      const response = await updateSponsoredUser({
        id: editTarget.id,
        body: updateBody,
      }).unwrap();
      if (response?.success) {
        enqueueSnackbar(response.message || "Beneficiary details updated successfully", { variant: "success" });
        setEditDialogOpen(false);
        setEditTarget(null);
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "Failed to update beneficiary details", { variant: "error" });
    }
  };

  const handleDeleteIndividual = async () => {
    if (!deleteTarget) return;
    try {
      const response = await deleteSponsoredUser(deleteTarget.id).unwrap();
      if (response?.success) {
        enqueueSnackbar(response.message || "Beneficiary deleted successfully", { variant: "success" });
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "Failed to delete beneficiary", { variant: "error" });
    }
  };

  const handleDeleteBulk = async () => {
    if (!selectedIdsForDelete.length) return;
    try {
      const response = await deleteSponsoredUsersBulk(selectedIdsForDelete).unwrap();
      if (response?.success) {
        enqueueSnackbar(response.message || "Selected beneficiaries deleted successfully", { variant: "success" });
        setBulkDeleteDialogOpen(false);
        setSelectedIdsForDelete([]);
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "Failed to delete selected beneficiaries", { variant: "error" });
    }
  };

  const isEditDirty = useMemo(() => {
    if (!editTarget) return false;
    return (
      editName !== (editTarget.name || "") ||
      editContactNumber !== (editTarget.contactNumber || "") ||
      editEmail !== (editTarget.email || "")
    );
  }, [editName, editContactNumber, editEmail, editTarget]);

  const actions = useMemo(() => {
    return {
      field: "actions",
      headerName: "Actions",
      width: isOrgAdmin ? 180 : isOrgOrSupport ? 100 : 160,
      renderCell: (params) => {
        const rowStatus = params.row.status?.toLowerCase();
        if (isOrgAdmin && rowStatus === "pending") {
          return (
            <Stack direction="row" spacing={1} sx={{ height: "100%", alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApproveDirectly(params.row);
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRejectDirectly(params.row);
                }}
              >
                Reject
              </Button>
            </Stack>
          );
        }

        return (
          <Stack direction="row" spacing={1} sx={{ height: "100%", alignItems: "center" }}>
            {!isOrgOrSupport && (
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDelete(params.row);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    };
  }, [isOrgAdmin, isOrgOrSupport, handleApproveDirectly, handleRejectDirectly, handleOpenEdit, handleOpenDelete]);

  const orgColumns = useMemo(() => [
    {
      field: "userId",
      headerName: "User ID",
      defaultVisible: true,
      isViewable: true,
      isSortable: true,
      isFilterable: true,
      isDownloadable: true,
      type: "string",
      valueGetter: (value, row) => row?.userId || row?.id || "--",
    },
    { field: "status", headerName: "Status", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "enum" },
    {
      field: "addedBTypeDisplay",
      headerName: "Added By",
      defaultVisible: true,
      isViewable: true,
      isSortable: true,
      isFilterable: true,
      isDownloadable: true,
      type: "string",
      width: 170,
      renderCell: (params) => {
        const addedBy = (params.row.addedBType || params.row.addedByType || "").toLowerCase();
        let icon = null;
        let label = "--";

        if (addedBy === "organization" || addedBy === "org") {
          icon = <Business fontSize="small" sx={{ color: "primary.main" }} />;
          label = "Organization";
        } else if (addedBy === "admin") {
          icon = <AdminPanelSettings fontSize="small" sx={{ color: "warning.main" }} />;
          label = "Admin";
        } else if (addedBy === "support") {
          icon = <AdminPanelSettings fontSize="small" sx={{ color: "secondary.main" }} />;
          label = "Support";
        }

        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: "100%" }}>
            {icon && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                {icon}
              </Box>
            )}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      defaultVisible: true,
      isViewable: true,
      isSortable: true,
      isFilterable: true,
      isDownloadable: true,
      type: "date",
      valueGetter: (value) => (value ? new Date(value) : null),
    },
  ], []);

  const supportColumns = useMemo(() => [
    {
      field: "userId",
      headerName: "User ID",
      defaultVisible: true,
      isViewable: true,
      isSortable: true,
      isFilterable: true,
      isDownloadable: true,
      type: "string",
      valueGetter: (value, row) => row?.userId || row?.id || "--",
    },
    { field: "organizationName", headerName: "Target Organization", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "string" },
    { field: "status", headerName: "Status", defaultVisible: true, isViewable: true, isSortable: true, isFilterable: true, isDownloadable: true, type: "enum" },
    {
      field: "createdAt",
      headerName: "Created Date",
      defaultVisible: true,
      isViewable: true,
      isSortable: true,
      isFilterable: true,
      isDownloadable: true,
      type: "date",
      valueGetter: (value) => (value ? new Date(value) : null),
    },
  ], []);

  const {
    data: organizations,
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
  } = useGetOrganizationsQuery();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStatusTabChange = (event, newValue) => {
    setStatusTabValue(newValue);
  };

  const handleResetExistentFilter = () => {
    setExistentOrgId("");
    setExistentStatus("");
    setAddedByFilter("all");
  };

  const handleResetNonExistentFilter = () => {
    setNonExistentOrgId("");
    setNonExistentStatus("");
  };

  const statusFilter = useMemo(() => {
    if (statusTabValue === 1) return "pending";
    if (statusTabValue === 2) return "in process";
    if (statusTabValue === 3) return "approved";
    if (statusTabValue === 4) return "rejected";
    return "";
  }, [statusTabValue]);

  // Existent list params
  const existentQueryParams = useMemo(() => {
    const params = {};
    if (isOrgAdmin) {
      if (userOrgId) params.organizationId = userOrgId;
      if (statusFilter) params.status = statusFilter;
      if (addedByFilter && addedByFilter !== "all") params.addedByType = addedByFilter;
    } else {
      if (existentOrgId) params.organizationId = existentOrgId;
      if (existentStatus) params.status = existentStatus;
      if (addedByFilter && addedByFilter !== "all") params.addedByType = addedByFilter;
    }
    return params;
  }, [isOrgAdmin, userOrgId, statusFilter, addedByFilter, existentOrgId, existentStatus]);

  // ── Status counts for the summary cards ──────────────────────────────────
  // Piggyback on the same RTK Query cache entry that powers the table below.
  // CustomTable calls useGetConsolidatedSponsoredUsersQuery(existentQueryParams)
  // internally; calling it here with identical args shares that cache — zero
  // extra network requests. The backend now returns statusCounts (DB-level
  // aggregation, ignores status/pagination) alongside the paginated records.
  const { data: tableQueryData } = useGetConsolidatedSponsoredUsersQuery(
    existentQueryParams,
    { skip: !isOrgAdmin },
  );

  const statusCounts = useMemo(() => {
    const sc = tableQueryData?.statusCounts;
    if (!sc) return { all: 0, pending: 0, "in process": 0, approved: 0, rejected: 0 };
    return {
      all:          sc.all        ?? 0,
      pending:      sc.pending    ?? 0,
      "in process": sc.in_process ?? 0,
      approved:     sc.approved   ?? 0,
      rejected:     sc.rejected   ?? 0,
    };
  }, [tableQueryData]);

  // Non-existent list params
  const nonExistentQueryParams = useMemo(() => {
    const params = {};
    if (nonExistentOrgId) params.organizationId = nonExistentOrgId;
    if (nonExistentStatus) params.status = nonExistentStatus;
    return params;
  }, [nonExistentOrgId, nonExistentStatus]);

  return (
    <Protected>
      <Helmet>
        <title>Beneficiaries | Smarton Dashboard</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            {isOrgAdmin
              ? "Sponsorship Beneficiaries"
              : tabValue === 0
                ? "Active Beneficiaries"
                : "Non-Existent Users"}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenForm(true)}
          >
            Add Beneficiary
          </Button>
        </Stack>

        {/* Status summary cards for Org Admin — replaces the old tab row */}
        {isOrgAdmin && (
          <StatusSummaryCards
            counts={statusCounts}
            selectedIndex={statusTabValue}
            onSelect={setStatusTabValue}
          />
        )}

        {/* Tab Controls (Visible to Admin and Support only) */}
        {isAdminOrSupport && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Active Beneficiaries" />
              <Tab label="Non-Existent Users" />
            </Tabs>
          </Box>
        )}

        {/* Tab 0 / Org Admin: Active & Consolidated Beneficiaries */}
        {(isOrgAdmin || tabValue === 0) && (
          <Card>
            <CustomTable
              canSelect
              showBulkDelete
              onBulkDelete={handleOpenBulkDelete}
              actions={actions}
              columnsOverride={isOrgAdmin ? orgColumns : isSupport ? supportColumns : undefined}
              useDataQuery={isOrgAdmin ? useGetConsolidatedSponsoredUsersQuery : useGetActiveSponsoredUsersQuery}
              searchLabel={isOrgOrSupport ? "Search by User ID..." : "Search Beneficiary..."}
              tableName="ActiveBeneficiaries"
              defaultQueryParams={existentQueryParams}
              restrictDownloadCols={[]}
              searchCols={isOrgOrSupport ? ["userId", "id"] : ["name", "email", "contactNumber"]}
              resetParentForm={handleResetExistentFilter}
              onRowClick={(params) => {
                setSelectedRow(params.row);
                setOpenDrawer(true);
              }}
              extraHeaderField={
                isOrgAdmin ? (
                  <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                    <Grid container spacing={2}>
                      {/* Added By Dropdown Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="added-by-label">Added By</InputLabel>
                          <Select
                            labelId="added-by-label"
                            id="added-by-select"
                            value={addedByFilter}
                            label="Added By"
                            onChange={(e) => setAddedByFilter(e.target.value)}
                          >
                            <MenuItem value="all">All Sources</MenuItem>
                            <MenuItem value="admin">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AdminPanelSettings fontSize="small" color="warning" />
                                <span>Added by Admin</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="support">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AdminPanelSettings fontSize="small" color="secondary" />
                                <span>Added by Support</span>
                              </Box>
                            </MenuItem>
                            <MenuItem value="organization">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Business fontSize="small" color="primary" />
                                <span>Added by Organization</span>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                    <Grid container spacing={2}>
                      {/* Organization Selector */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="existent-org-label">Organization</InputLabel>
                          <Select
                            labelId="existent-org-label"
                            id="existent-org"
                            value={existentOrgId}
                            label="Organization"
                            onChange={(e) => setExistentOrgId(e.target.value)}
                          >
                            <MenuItem value="">All Organizations</MenuItem>
                            {isOrgLoading || isOrgFetching ? (
                              <MenuItem disabled>
                                <CircularProgress size={20} />
                              </MenuItem>
                            ) : (
                              organizations?.data?.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                  {org.name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Verification Status Filter */}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="existent-status-label">Verification Status</InputLabel>
                          <Select
                            labelId="existent-status-label"
                            id="existent-status"
                            value={existentStatus}
                            label="Verification Status"
                            onChange={(e) => setExistentStatus(e.target.value)}
                          >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                )
              }
            />
          </Card>
        )}

        {/* Tab 1: Non-Existent Users (Admin/Support only) */}
        {isAdminOrSupport && tabValue === 1 && (
          <Card>
            <CustomTable
              canSelect
              showBulkDelete
              onBulkDelete={handleOpenBulkDelete}
              actions={actions}
              columnsOverride={isSupport ? supportColumns : undefined}
              useDataQuery={useGetNonExistentSponsoredUsersQuery}
              searchLabel={isOrgOrSupport ? "Search by User ID..." : "Search Non-Existent Users..."}
              tableName="NonExistentBeneficiaries"
              defaultQueryParams={nonExistentQueryParams}
              restrictDownloadCols={[]}
              searchCols={isOrgOrSupport ? ["userId", "id"] : ["name", "email", "contactNumber"]}
              resetParentForm={handleResetNonExistentFilter}
              isSupport={isSupport}
              extraHeaderField={
                <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                  <Grid container spacing={2}>
                    {/* Organization Selector */}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="nonexistent-org-label">Organization</InputLabel>
                        <Select
                          labelId="nonexistent-org-label"
                          id="nonexistent-org"
                          value={nonExistentOrgId}
                          label="Organization"
                          onChange={(e) => setNonExistentOrgId(e.target.value)}
                        >
                          <MenuItem value="">All Organizations</MenuItem>
                          {isOrgLoading || isOrgFetching ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} />
                            </MenuItem>
                          ) : (
                            organizations?.data?.map((org) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Verification Status Filter */}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="nonexistent-status-label">Verification Status</InputLabel>
                        <Select
                          labelId="nonexistent-status-label"
                          id="nonexistent-status"
                          value={nonExistentStatus}
                          label="Verification Status"
                          onChange={(e) => setNonExistentStatus(e.target.value)}
                        >
                          <MenuItem value="">All Statuses</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="in process">In Process</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              }
            />
          </Card>
        )}
      </Container>

      <AddBeneficiaryForm
        open={openForm}
        onClose={() => setOpenForm(false)}
      />

      {/* Sponsorship Details Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: { width: 400, border: "none", overflow: "hidden" },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1">Sponsorship Details</Typography>
              <IconButton onClick={() => setOpenDrawer(false)}>
                <Close />
              </IconButton>
            </Box>
            <Divider />

            {selectedRow && (
              <Stack spacing={3.5}>
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    User ID
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 0.5, wordBreak: "break-all" }}>
                    {selectedRow.userId || selectedRow.id || "--"}
                  </Typography>
                </Box>

                {!isOrgOrSupport && (
                  <>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Beneficiary Name
                      </Typography>
                      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                        {selectedRow.name || "--"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Email Address
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedRow.email || "--"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Contact Number
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedRow.contactNumber || "--"}
                      </Typography>
                    </Box>
                  </>
                )}

                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Label
                      color={
                        selectedRow.status?.toLowerCase() === "approved"
                          ? "success"
                          : selectedRow.status?.toLowerCase() === "rejected"
                          ? "error"
                          : "warning"
                      }
                    >
                      {selectedRow.status}
                    </Label>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Added By
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedRow.addedBTypeDisplay || "--"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Created Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedRow.createdAt ? new Date(selectedRow.createdAt).toLocaleDateString() : "--"}
                  </Typography>
                </Box>

                {isOrgAdmin && selectedRow.status?.toLowerCase() === "pending" && (
                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <LoadingButton
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handleApproveDirectly(selectedRow)}
                      loading={isApproving}
                    >
                      Approve
                    </LoadingButton>
                    <LoadingButton
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => handleRejectDirectly(selectedRow)}
                      loading={isRejecting}
                    >
                      Reject
                    </LoadingButton>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Individual Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Beneficiary</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this beneficiary? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteIndividual}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title">Delete Multiple Beneficiaries</DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-delete-dialog-description">
            Are you sure you want to delete the selected {selectedIdsForDelete.length} beneficiaries? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteBulk}
            color="error"
            variant="contained"
            disabled={isBulkDeleting}
          >
            {isBulkDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Beneficiary Modal */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditErrors({}); }}
        aria-labelledby="edit-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="edit-dialog-title">Edit Beneficiary Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
                if (editErrors.editName) setEditErrors((p) => ({ ...p, editName: "" }));
              }}
              error={!!editErrors.editName}
              helperText={editErrors.editName || "Letters and spaces only — 2 to 50 characters"}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Contact Number"
              value={editContactNumber}
              onChange={(e) => {
                setEditContactNumber(e.target.value);
                if (editErrors.editContactNumber) setEditErrors((p) => ({ ...p, editContactNumber: "" }));
              }}
              error={!!editErrors.editContactNumber}
              helperText={editErrors.editContactNumber || "10-digit mobile number"}
              fullWidth
              size="small"
              inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
            />
            <TextField
              label="Email"
              value={editEmail}
              onChange={(e) => {
                setEditEmail(e.target.value);
                if (editErrors.editEmail) setEditErrors((p) => ({ ...p, editEmail: "" }));
              }}
              error={!!editErrors.editEmail}
              helperText={editErrors.editEmail || "e.g. rahul@example.com"}
              fullWidth
              size="small"
              inputProps={{ maxLength: 254 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setEditErrors({}); }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            color="primary"
            variant="contained"
            disabled={!isEditDirty || isUpdating}
          >
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reason for Rejection</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter the reason for rejecting {isOrgOrSupport ? (rejectRow?.userId || rejectRow?.id || "this beneficiary") : (rejectRow?.name || "this beneficiary")}.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            variant="outlined"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={confirmRejectDirectly}
            color="error"
            variant="contained"
            loading={isRejecting}
            disabled={!rejectReason.trim()}
          >
            Reject
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Protected>
  );
}

